import React from 'react';

import { Alert } from 'react-bootstrap';

import Button from 'shared/button';
import Contacts from '../contacts';
import DateFormGroup from 'bundles/apex/dates/form_group';
import ImmediateRemindersModal from 'bundles/apex/deals/show/critical_dates/modal/immediate_reminders';
import ManagedFormGroup from 'components/form/managed_input/form_group';
import Modal from 'components/modal';
import Options from 'components/form/managed_input/options';
import RemindersFields from 'bundles/apex/reminders/reminders_fields';
import Snackbar from 'shared/snackbar';
import { useTaskModalState } from './helpers';

import type { ModalOptions, Task, TaskAttributes } from '../types';
import type { Reminder } from 'bundles/apex/reminders/types';

import 'bundles/apex/tasks/styles.scss';

export interface Props {
  checkImmediateRemindersPath?: string;
  createTask?: (task: TaskAttributes) => Promise<any>;  
  edgeCrm?: boolean;
  formDependenciesPath?: string;
  hideAssignee?: boolean;
  options?: ModalOptions;
  reminders?: Reminder[];
  show: boolean;
  showType?: boolean;
  task?: Task,
  updateTask?: (task: TaskAttributes) => Promise<any>;
  onHide: () => void;
}

export default function TaskModalStandard({
  checkImmediateRemindersPath,
  createTask,
  edgeCrm,
  formDependenciesPath,
  hideAssignee,
  options,
  reminders,
  show,
  showType,
  task,
  updateTask,
  onHide,
}: Props) {
  const {
    dependencies,
    dependenciesError,
    errors,
    formTask,
    handleAdd,
    handleChange,
    handleRemindersChange,
    hide,
    immediateReminders,
    loading,
    reset,
    requestReminders,
    saving,
    setImmediateReminders,
    typeOptions,
    userNames,
  } = useTaskModalState({
    checkImmediateRemindersPath,
    createTask,
    formDependenciesPath,
    options,
    reminders,
    show,
    updateTask,
    onHide
  });
  const { assignableUsers, contacts, deal, fields } = (dependencies || { assignableUsers: [], contacts: [] });

  return <>
    <Modal className="visible-overflow" show={show && !loading} onHide={hide} onShow={() => reset(task)}>
      <Modal.Header closeButton>
        {formTask.id ? 'Edit Task' : 'Add Task'}
      </Modal.Header>

      <Modal.Body>
        {errors && <Alert bsStyle="warning">
          {errors.map((error, i) => (
            <div key={i}>
              {error}
            </div>
          ))}
        </Alert>}
        <div className="form form-horizontal">
          {showType &&
            <ManagedFormGroup
              label="Type"
              name="taskType"
              type="select"
              value={formTask.taskType}
              onChange={handleChange}
            >
              <Options options={typeOptions} />
            </ManagedFormGroup>
          }
          {options?.parents && options.parents.length > 1 &&
            <ManagedFormGroup
              includeBlank
              label="Listing"
              name="parentOptionValue"
              required
              type="select"
              value={formTask.parentOptionValue}
              onChange={handleChange}
            >
              <Options options={options.parents} />
            </ManagedFormGroup>
          }

          <ManagedFormGroup
            label="Task Name"
            name="name"
            required
            value={formTask.name}
            onChange={handleChange}
          />

          {!hideAssignee &&
            <ManagedFormGroup
              includeBlank
              label="Assigned To"
              name="assigneeId"
              type="select"
              value={formTask.assigneeId}
              onChange={handleChange}
            >
              <Options
                labelName="name"
                options={assignableUsers}
                valueName="id"
              />
            </ManagedFormGroup>
          }

          {deal &&
            <DateFormGroup
              deal={deal}
              fields={fields}
              label="Due Date"
              name="deadline"
              value={formTask.deadline}
              onChange={handleChange}
            />
          }

          {!deal &&
            <ManagedFormGroup
              label="Due Date"
              name="deadline"
              required
              type="date"
              value={formTask.deadline?.date}
              onValueChange={date => handleChange({ deadline: { ...formTask.deadline, date, static: true } })}
            />
          }

          <ManagedFormGroup
            disabled={!!task}
            label="Require Attachment"
            name="requiresAttachment"
            type="select"
            value={formTask.requiresAttachment}
            onChange={handleChange}
          >
            <option key="no" value="false">
              No
            </option>
            <option key="yes" value="true">
              Yes
            </option>
          </ManagedFormGroup>

          {edgeCrm && (
            <ManagedFormGroup
              label="Notes"
              name="notes"
              type="textarea"
              value={formTask.notes}
              onChange={handleChange}
            />
          )}

          <RemindersFields
            dateNameWhitelist={['taskName']}
            deal={{ ...deal, taskName: formTask.deadline }}
            fields={{
              ...fields,
              taskName: { label: formTask.name || 'task', isDate: true },
            }}
            reminders={requestReminders}
            taskName={formTask.name}
            onChange={handleRemindersChange}
          />
          <ImmediateRemindersModal
            handleSelection={handleAdd}
            show={show && immediateReminders.length > 0}
            userNames={userNames}
            onHide={() => setImmediateReminders([])}
          />

          {edgeCrm &&
            <Contacts
              contacts={contacts}
              contactsTasks={formTask.contactsTasks}
              onChange={contactsTasks => handleChange({ contactsTasks })}
            />
          }
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button.Cancel name="cancel" onClick={hide} />
        <Button.Primary
          disabled={!formTask.name || saving}
          name="save"
          onClick={() => handleAdd()}
        >
          Save Task
        </Button.Primary>
      </Modal.Footer>
    </Modal>
    <Snackbar show={dependenciesError} text="Something went wrong" variant="danger" />
  </>;
}
